import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { User, Globe, DollarSign, ExternalLink, Clock, HelpCircle, X, Link, Settings, Loader2, Users, Smartphone, MessageCircle, LogOut } from 'lucide-react'
import useAppStore from '../store/useAppStore'
import useAppEnvironment from '../hooks/useAppEnvironment'
import apiService from '../services/api'
import { userApi } from '../services'
import { openSupport } from '../services/supportService'
import toast from 'react-hot-toast'
import TopSkinsShowcase from '../components/TopSkinsShowcase'

const Profile = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, language, isLanguageLoading, changeLanguage, loadProfile } = useAppStore()
  const { isTelegramMiniApp, isWebBrowser } = useAppEnvironment()
  const [tradeLink, setTradeLink] = useState('')
  const [showTradeLinkModal, setShowTradeLinkModal] = useState(false)
  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const [showCurrencyModal, setShowCurrencyModal] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)
  const [isSavingTradeLink, setIsSavingTradeLink] = useState(false)
  const [currencies, setCurrencies] = useState([])
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false)
  const [isUpdatingCurrency, setIsUpdatingCurrency] = useState(false)
  const [homeScreenStatus, setHomeScreenStatus] = useState('unknown')

  // Проверка статуса шортката при монтировании
  useEffect(() => {
    // Проверяем версию WebApp API (метод доступен с версии 8.0)
    const webAppVersion = window.Telegram?.WebApp?.version || '6.0'
    const [major] = webAppVersion.split('.').map(Number)
    
    if (major >= 8 && window.Telegram?.WebApp?.checkHomeScreenStatus) {
      try {
        window.Telegram.WebApp.checkHomeScreenStatus((status) => {
          setHomeScreenStatus(status)
        })
      } catch (error) {
        console.log('checkHomeScreenStatus не поддерживается:', error)
        setHomeScreenStatus('unsupported')
      }
    } else {
      // Для версий ниже 8.0 помечаем как неподдерживаемое
      setHomeScreenStatus('unsupported')
    }
  }, [])

  // Обработка добавления на главный экран
  const handleAddToHomeScreen = useRef(null)
  
  useEffect(() => {
    let isSubscribed = false
    
    handleAddToHomeScreen.current = () => {
      // Проверяем версию WebApp API
      const webAppVersion = window.Telegram?.WebApp?.version || '6.0'
      const [major] = webAppVersion.split('.').map(Number)
      
      if (major < 8) {
        toast.error(t('profile.homeScreenNotSupported'))
        return
      }
      
      if (window.Telegram?.WebApp?.addToHomeScreen) {
        try {
          window.Telegram.WebApp.addToHomeScreen()
          
          // Подписываемся на событие успешного добавления (только если еще не подписаны)
          if (!isSubscribed) {
            isSubscribed = true
            
            const handleHomeScreenAdded = () => {
              setHomeScreenStatus('added')
              toast.success(t('profile.addedToHomeScreen'))
              // Отписываемся сразу после первого срабатывания
              window.Telegram.WebApp.offEvent('homeScreenAdded', handleHomeScreenAdded)
              isSubscribed = false
            }
            
            window.Telegram.WebApp.onEvent('homeScreenAdded', handleHomeScreenAdded)
          }
        } catch (error) {
          console.error('Ошибка при добавлении на главный экран:', error)
          toast.error(t('profile.addToHomeScreenError'))
        }
      } else {
        toast.error(t('profile.homeScreenNotSupported'))
      }
    }
    
    return () => {
      isSubscribed = false
    }
  }, [t])

  // Обработка смены языка с API вызовом
  const handleLanguageChange = async (newLanguage) => {
    try {
      // Закрываем модалку сразу
      setShowLanguageModal(false)
      
      // Используем новый метод из store для смены языка
      await changeLanguage(newLanguage, i18n.changeLanguage)
      
      // Показываем уведомление об успешной смене языка
      toast.success(t('profile.languageChanged'))
    } catch (error) {
      // Показываем ошибку пользователю
      const errorMessage = error.message || t('profile.languageChangeError') || 'Не удалось изменить язык'
      toast.error(errorMessage)
      console.error('Ошибка смены языка в Profile:', error)
    }
  }

  // Загрузка профиля при монтировании компонента
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setIsLoadingProfile(true)
        
        // Используем метод из store для синхронизации языка
        const response = await loadProfile(i18n.changeLanguage)
        
        // Предзаполняем поле трейдлинка если он есть
        if (response && response.me && response.me.tradelink) {
          setTradeLink(response.me.tradelink)
        }
      } catch (error) {
        console.error('Ошибка загрузки профиля:', error)
        toast.error(t('profile.profileLoadError'))
      } finally {
        setIsLoadingProfile(false)
      }
    }

    loadUserProfile()
  }, []) // Убираем loadProfile из зависимостей чтобы избежать циклических вызовов

  // Автооткрытие модалки трейдлинка при переходе из инвентаря
  useEffect(() => {
    const openTradeLink = searchParams.get('openTradeLink')
    if (openTradeLink === 'true' && user) {
      // Предзаполняем поле трейдлинка если он есть
      if (user.tradelink) {
        setTradeLink(user.tradelink)
      }
      setShowTradeLinkModal(true)
      
      // Очищаем URL параметр после открытия модалки
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('openTradeLink')
      navigate(`/profile?${newSearchParams.toString()}`, { replace: true })
    }
  }, [user, searchParams])

  // Обработка сохранения трейдлинка
  const handleSaveTradeLink = async () => {
    try {
      setIsSavingTradeLink(true)
      
      // Используем метод из userApi с встроенной валидацией
      await userApi.updateTradeUrl(tradeLink)
      
      setShowTradeLinkModal(false)
      toast.success(t('profile.tradeLinkSaved'))
    } catch (error) {
      toast.error(error.message || t('profile.tradeLinkError'), {
        duration: 3000
      })
    } finally {
      setIsSavingTradeLink(false)
    }
  }

  // Обработка открытия модалки с предзаполнением
  const handleOpenTradeLinkModal = () => {
    // Если у пользователя уже есть трейдлинк, предзаполняем поле
    if (user?.tradelink) {
      setTradeLink(user.tradelink)
    }
    setShowTradeLinkModal(true)
  }

  // Список поддерживаемых языков (названия на родном языке)
  const supportedLanguages = [
    { code: 'en', name: 'English' },
    { code: 'ru', name: 'Русский' },
    { code: 'uk', name: 'Українська' },
    { code: 'pt', name: 'Português' },
    { code: 'es', name: 'Español' },
    { code: 'tr', name: 'Türkçe' },
    { code: 'kk', name: 'Қазақша' },
    { code: 'uz', name: 'O\'zbekcha' },
    { code: 'id', name: 'Bahasa Indonesia' }
  ]

  // Открытие модалки выбора языка
  const handleOpenLanguageModal = () => {
    setShowLanguageModal(true)
  }

  const handleOpenCurrencyModal = async () => {
    try {
      setShowCurrencyModal(true)
      setIsLoadingCurrencies(true)

      const response = await userApi.getCurrencies()
      setCurrencies(response)
    } catch (error) {
      toast.error(error.message || 'error backend')
    } finally {
      setIsLoadingCurrencies(false)
    }
  }

  const handleCurrencyChange = async (currencyCode) => {
    try {
      if (!currencyCode || isUpdatingCurrency) {
        return
      }

      const currentCurrencyCode = user?.currency?.code
      if (currentCurrencyCode === currencyCode) {
        setShowCurrencyModal(false)
        return
      }

      setIsUpdatingCurrency(true)
      await userApi.updateCurrency(currencyCode)
      await loadProfile(i18n.changeLanguage)

      setShowCurrencyModal(false)
    } catch (error) {
      toast.error(error.message || 'error backend')
    } finally {
      setIsUpdatingCurrency(false)
    }
  }

  const getCurrentLanguageName = () => {
    const currentLang = supportedLanguages.find(lang => lang.code === language)
    return currentLang ? currentLang.name : language
  }

  // Проверяем доступность метода addToHomeScreen
  const isHomeScreenAvailable = () => {
    const webAppVersion = window.Telegram?.WebApp?.version || '6.0'
    const [major] = webAppVersion.split('.').map(Number)
    return major >= 8 && window.Telegram?.WebApp?.addToHomeScreen
  }

  const getWebVersionLink = () => {
    if (!user?.web) {
      return null
    }

    const telegramInitData = window.Telegram?.WebApp?.initData

    if (telegramInitData && user.web.endsWith('#tgWebAppData=')) {
      return `${user.web}${encodeURIComponent(telegramInitData)}`
    }

    return user.web
  }

  const handleLogout = () => {
    // Очищаем browser-auth состояние, включая cookie и сохраненные Telegram init params
    apiService.clearBrowserAuthState()
    window.location.replace('/welcome')
  }

  const handleOpenWebVersion = () => {
    const webVersionLink = getWebVersionLink()

    if (!webVersionLink) {
      return
    }

    apiService.get('/api/send_message/skinoz_pro/').catch((error) => {
      console.error('Ошибка отправки сообщения для SKINOZ PRO:', error)
    })

    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(webVersionLink)
      return
    }

    window.open(webVersionLink, '_blank', 'noopener,noreferrer')
  }

  const handleOpenTelegramApp = () => {
    const telegramAppLink = 'https://t.me/skinozbot'

    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(telegramAppLink)
      return
    }

    window.open(telegramAppLink, '_blank', 'noopener,noreferrer')
  }

  const profileSections = [
    {
      title: t('profile.settings'),
      items: [
        {
          icon: Globe,
          label: t('profile.language'),
          value: isLanguageLoading ? t('profile.changing') || 'Изменяется...' : getCurrentLanguageName(),
          onClick: isLanguageLoading ? null : handleOpenLanguageModal,
          isLoading: isLanguageLoading
        },
        {
          icon: Link,
          label: t('profile.tradeLink'),
          value: user?.tradelink ? t('profile.change') : t('profile.setup'),
          onClick: handleOpenTradeLinkModal
        },
        {
          icon: DollarSign,
          label: user?.currency?.code || '---',
          value: user?.currency?.string || null,
          onClick: handleOpenCurrencyModal
        }
      ]
    },
    {
      title: t('profile.additional'),
      items: [
        {
          icon: Users,
          label: t('referrals.title'),
          onClick: () => navigate('/referrals')
        },
        {
          icon: Clock,
          label: t('profile.transactions'),
          onClick: () => navigate('/transactions')
        },
        // Показываем кнопку только если метод доступен И еще не добавлено
        ...(isHomeScreenAvailable() && homeScreenStatus !== 'added' ? [{
          icon: Smartphone,
          label: t('profile.addToHomeScreen'),
          onClick: () => handleAddToHomeScreen.current?.()
        }] : []),
        // Показываем кнопку сообщества только если есть ссылка в профиле
        ...(user?.tg_channel ? [{
          icon: Users,
          label: t('profile.community'),
          onClick: () => window.open(user.tg_channel, '_blank')
        }] : []),
        // Показываем кнопку чата только если есть ссылка в профиле
        ...(user?.tg_chat ? [{
          icon: MessageCircle,
          label: t('profile.chat'),
          onClick: () => window.open(user.tg_chat, '_blank')
        }] : []),
        {
          icon: HelpCircle,
          label: t('profile.support'),
          onClick: () => openSupport(user?.telegramid ?? user?.id ?? null)
        }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-dark-primary text-white">
      <div className="mx-auto w-full lg:max-w-6xl lg:px-6 lg:py-8">
        <div className="lg:grid lg:grid-cols-12 lg:items-start lg:gap-8">
          <div className="lg:col-span-4 lg:space-y-6 lg:sticky lg:top-24">
            {/* Header */}
            <div className="p-6 border-b border-white border-opacity-10 lg:rounded-2xl lg:border lg:bg-surface/30">
              {/* <h1 className="text-3xl font-bold text-accent-primary mb-4 flex items-center gap-3 justify-center">
                <User size={32} />
                {t('profile.title')}
              </h1> */}
        {/* User Info */}
        {isLoadingProfile ? (
          <div className="">
            <div className="flex flex-col items-center">
              {/* Skeleton для аватарки */}
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-accent-primary/30 mb-3">
                <div className="w-full h-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center">
                  <Loader2 size={36} className="text-dark-primary animate-spin" />
                </div>
              </div>
              <div className="text-center">
                {/* Skeleton для имени */}
                <div className="h-7 bg-gray-600 rounded animate-pulse w-32 mx-auto"></div>
              </div>
            </div>
          </div>
        ) : user ? (
          <div className="">
            <div className="flex flex-col items-center">
              {/* Аватарка пользователя */}
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-accent-primary/30 mb-3">
                {user.ava ? (
                  <img 
                    src={user.ava} 
                    alt={`${user.firstname} ${user.lastname || ''}`.trim()}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback к градиентному плейсхолдеру при ошибке загрузки
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'flex'
                    }}
                  />
                ) : null}
                {/* Fallback плейсхолдер */}
                <div 
                  className={`w-full h-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center ${user.ava ? 'hidden' : 'flex'}`}
                  style={{ display: user.ava ? 'none' : 'flex' }}
                >
                  <User size={36} className="text-dark-primary" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-white">
                  {user.firstname || ''}
                </h3>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-surface rounded-lg p-4 border border-white border-opacity-10">
            <p className="text-text-secondary">{t('profile.profileLoadError')}</p>
          </div>
        )}
      </div>

      {/* Top Skins Showcase */}
      <div className="">
        {isLoadingProfile ? (
          <TopSkinsShowcase isLoading={true} />
        ) : user?.top_skin ? (
          <TopSkinsShowcase topSkins={user.top_skin} />
        ) : null}
      </div>
          </div>

      {/* Profile Sections */}
          <div className="lg:col-span-8">
      <div className="p-6 space-y-6 lg:p-0">
        {profileSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="">
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Settings size={20} />
              {section.title}
            </h2>
            
            <div className="space-y-2">
              {section.items.map((item, itemIndex) => {
                const Icon = item.icon
                const isItemLoading = item.isLoading || false
                const isDisabled = !item.onClick || isItemLoading || item.disabled
                
                return (
                  <button
                    key={itemIndex}
                    onClick={isDisabled ? undefined : item.onClick}
                    disabled={isDisabled}
                    className={`w-full bg-surface rounded-lg p-4 border border-white border-opacity-10 transition-all text-left ${
                      isDisabled 
                        ? 'opacity-60 cursor-not-allowed' 
                        : 'hover:border-accent-primary/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isItemLoading ? (
                          <Loader2 size={20} className="text-accent-primary animate-spin" />
                        ) : (
                          <Icon size={20} className="text-accent-primary" />
                        )}
                        <span className="font-medium text-white">{item.label}</span>
                      </div>
                      {item.value && (
                        <div className="flex items-center gap-2">
                          <span className="text-text-secondary">{item.value}</span>
                          {isItemLoading && (
                            <Loader2 size={16} className="text-text-secondary animate-spin" />
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        ))}

        {isTelegramMiniApp && user?.web && (
          <button
            onClick={handleOpenWebVersion}
            className="mb-3 w-full rounded-lg border border-accent-primary/30 bg-accent-primary/10 px-4 py-4 text-left font-medium text-accent-primary transition-all hover:border-accent-primary/50 hover:bg-accent-primary/15"
          >
            <div className="flex items-center gap-3">
              <ExternalLink size={20} className="text-accent-primary" />
              <span>SKINOZ PRO</span>
            </div>
          </button>
        )}

        {isWebBrowser && (
          <button
            onClick={handleOpenTelegramApp}
            className="mb-3 w-full rounded-lg border border-accent-primary/30 bg-accent-primary/10 px-4 py-4 text-left font-medium text-accent-primary transition-all hover:border-accent-primary/50 hover:bg-accent-primary/15"
          >
            <div className="flex items-center gap-3">
              <ExternalLink size={20} className="text-accent-primary" />
              <span>SKINOZ Telegram App</span>
            </div>
          </button>
        )}

        {isWebBrowser && (
          <button
            onClick={handleLogout}
            className="w-full rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-4 text-left font-medium text-red-400 transition-all hover:border-red-400/50 hover:bg-red-500/15"
          >
            <div className="flex items-center gap-3">
              <LogOut size={20} className="text-red-400" />
              <span>{t('profile.logout')}</span>
            </div>
          </button>
        )}
      </div>
          </div>
        </div>
      </div>

      {/* Trade Link Modal */}
      {showTradeLinkModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-6 z-50">
          <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-white border-opacity-10">
            <h3 className="text-xl font-bold text-white mb-4">
              {t('profile.updateTradeLink')}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-secondary mb-2">
                {t('profile.steamTradeLink')}
              </label>
              <input
                type="url"
                value={tradeLink}
                onChange={(e) => setTradeLink(e.target.value)}
                placeholder="https://steamcommunity.com/tradeoffer/new/..."
                className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg px-4 py-3 text-white focus:border-accent-primary focus:outline-none"
              />
              
              {/* Подсказка с ссылкой на Steam */}
              <div className="mt-3 p-3 bg-accent-primary/10 border border-accent-primary/20 rounded-lg">
                <p className="text-sm text-text-secondary mb-2">
                  {t('profile.tradeLinkHint')}
                </p>
                <a 
                  href="https://steamcommunity.com/id/me/tradeoffers/privacy#trade_offer_access_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-accent-primary hover:text-accent-secondary transition-colors text-sm font-medium"
                >
                  {t('profile.getSteamTradeLink')}
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
            
            {/* Важное предупреждение */}
            <div className="mb-4 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
              <p className="text-orange-400 text-sm font-medium">
                ⚠️ {t('profile.tradeLinkWarning')}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowTradeLinkModal(false)}
                disabled={isSavingTradeLink}
                className="flex-1 py-3 bg-transparent border border-text-secondary text-text-secondary rounded-lg hover:bg-text-secondary/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveTradeLink}
                disabled={isSavingTradeLink}
                className="flex-1 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSavingTradeLink ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t('profile.saving')}
                  </>
                ) : (
                  t('common.save')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-6 z-50">
          <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-white border-opacity-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">
                {t('profile.language')}
              </h3>
              <button
                onClick={() => setShowLanguageModal(false)}
                className="text-text-secondary hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {supportedLanguages.map((lang) => {
                const isSelected = language === lang.code
                const isLoading = isLanguageLoading && isSelected
                
                return (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    disabled={isLanguageLoading}
                    className={`w-full p-4 rounded-lg border transition-all text-left ${
                      isSelected
                        ? 'bg-accent-primary/20 border-accent-primary text-white'
                        : 'bg-dark-primary border-white border-opacity-10 text-text-secondary hover:border-accent-primary/30 hover:text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{lang.name}</span>
                      {isLoading ? (
                        <Loader2 size={20} className="text-accent-primary animate-spin" />
                      ) : isSelected ? (
                        <div className="w-5 h-5 rounded-full bg-accent-primary flex items-center justify-center hidden">
                          <div className="w-2 h-2 rounded-full bg-dark-primary" />
                        </div>
                      ) : null}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {showCurrencyModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-6 z-50">
          <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-white border-opacity-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">
                {user?.currency?.string || user?.currency?.code || '---'}
              </h3>
              <button
                onClick={() => setShowCurrencyModal(false)}
                className="text-text-secondary hover:text-white transition-colors"
                disabled={isUpdatingCurrency}
              >
                <X size={24} />
              </button>
            </div>

            {isLoadingCurrencies ? (
              <div className="py-8 flex items-center justify-center">
                <Loader2 size={24} className="text-accent-primary animate-spin" />
              </div>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {currencies.map((currency) => {
                  const isSelected = currency.select === true || user?.currency?.code === currency.code
                  const isLoading = isUpdatingCurrency && isSelected

                  return (
                    <button
                      key={currency.code}
                      onClick={() => handleCurrencyChange(currency.code)}
                      disabled={isUpdatingCurrency}
                      className={`w-full p-4 rounded-lg border transition-all text-left ${
                        isSelected
                          ? 'bg-accent-primary/20 border-accent-primary text-white'
                          : 'bg-dark-primary border-white border-opacity-10 text-text-secondary hover:border-accent-primary/30 hover:text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-medium truncate">{currency.string || currency.code}</span>
                        {isLoading ? <Loader2 size={20} className="text-accent-primary animate-spin shrink-0" /> : null}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Profile
